/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { BrandCoAgentInput } from "../types";
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

/**
 * Brand-research SSE pipeline runner. Posts to
 * `/core/coagent/brand-research` and publishes typed `brand:*` events on
 * the department event bus. The store wires
 * `wireBrandSubscriber` once at module init to translate events into the
 * actual mutations the legacy `SSECallbacks` ladder used to perform.
 *
 * Internal flag: `pipelineCompleted` distinguishes graceful end-of-stream
 * (after `phase=done` or `phase=error`) from an unexpected disconnect.
 * `onStreamEnd` publishes `brand:stream-ended-incomplete` only on the
 * disconnect path.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 72.
 */
export async function runBrandResearchSSE(
  brandInput: BrandCoAgentInput,
  sessionId: string,
  signal?: AbortSignal,
  options?: { targetBrandId?: string },
): Promise<void> {
  let pipelineCompleted = false;
  const targetBrandId = options?.targetBrandId;

  await runSSEStream(brandInput, {
    pipeline: "brand-research",
    endpoint: "/core/coagent/brand-research",
    sessionId,
    buildBody: (sid, input) => ({
      // Empty sessionId means a refresh-from-detail-page run that
      // intentionally avoids creating a Django session — the route
      // accepts sessionId-less calls when targetBrandId is provided.
      ...(sid ? { sessionId: sid } : {}),
      brandInput: input,
      ...(targetBrandId ? { targetBrandId } : {}),
    }),
    errorPrefix: "Brand research",
    signal,
    debugMeta: {
      brandName: brandInput.brandName,
      hasAbortSignal: Boolean(signal),
      ...(targetBrandId ? { targetBrandId } : {}),
    },
    onStreamEnd: () => {
      if (!pipelineCompleted) {
        sseEvents.publish({
          type: "brand:stream-ended-incomplete",
          pipeline: "brand-research",
          sessionId,
        });
      }
      return pipelineCompleted;
    },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details, progress } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;

      if (phase === "session_named" && ev?.name) {
        sseEvents.publish({
          type: "brand:session-named",
          pipeline: "brand-research",
          sessionId,
          name: ev.name as string,
        });
        return;
      }

      if (phase === "website_scrape" && status === "running") {
        sseEvents.publish({
          type: "brand:website-scrape-running",
          pipeline: "brand-research",
          sessionId,
          url: typeof ev?.url === "string" ? ev.url : "",
          data: ev ?? {},
        });
        return;
      }

      if (phase === "website_scrape" && status === "complete") {
        sseEvents.publish({
          type: "brand:website-scrape-complete",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "website_scrape" && status === "skipped") {
        sseEvents.publish({
          type: "brand:website-scrape-skipped",
          pipeline: "brand-research",
          sessionId,
          reason: typeof ev?.reason === "string" ? ev.reason : "unknown",
          data: ev ?? {},
        });
        return;
      }

      if (phase === "query_generation" && status === "running") {
        sseEvents.publish({
          type: "brand:query-generation-running",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "query_generation" && status === "complete") {
        sseEvents.publish({
          type: "brand:query-generation-complete",
          pipeline: "brand-research",
          sessionId,
          queryCount: Number(ev?.query_count ?? 0),
          data: ev ?? {},
        });
        return;
      }

      if (phase === "collection" && status === "running") {
        sseEvents.publish({
          type: "brand:collection-running",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
          progress,
        });
        return;
      }

      if (phase === "collection" && status === "complete") {
        sseEvents.publish({
          type: "brand:collection-complete",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "distillation" && status === "running") {
        sseEvents.publish({
          type: "brand:distillation-running",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "distillation" && status === "complete") {
        sseEvents.publish({
          type: "brand:distillation-complete",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "synthesis" && status === "running") {
        sseEvents.publish({
          type: "brand:synthesis-running",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "synthesis" && status === "complete") {
        sseEvents.publish({
          type: "brand:synthesis-complete",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "brand_generation" && status === "running") {
        sseEvents.publish({
          type: "brand:brand-generation-running",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "brand_generation" && status === "complete") {
        sseEvents.publish({
          type: "brand:brand-generation-complete",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "logo_generation" && status === "idle") {
        sseEvents.publish({
          type: "brand:logo-generation-idle",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        pipelineCompleted = true;
        sseEvents.publish({
          type: "brand:done",
          pipeline: "brand-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "error") {
        pipelineCompleted = true;
        const eventError = error ?? ev?.error ?? ev?.message;
        const eventDetails = details ?? ev?.details;
        const message = buildErrorMessage(
          eventError,
          eventDetails,
          "Brand research pipeline failed",
        );
        console.error("[coagent/brand-research] SSE error", {
          sessionId,
          message,
          details: eventDetails,
        });
        sseEvents.publish({
          type: "brand:error",
          pipeline: "brand-research",
          sessionId,
          message,
          ...(eventDetails !== undefined ? { details: eventDetails } : {}),
        });
      }
    },
  });
}
